import React from 'react'
import DialogWrapper from '../../../../components/core-ui/DialogWrapper'
import { Autocomplete, Box, Button, FormControl, TextField, Typography } from '@mui/material'
import { textFieldStyle } from '../../components/CreateProcessTemplateDialog'
import { HTTP_METHOD } from '../../../../utility/constants'
import PayloadEditor from './PayloadEditor'

const AddOutboundServerAction = ({
  setShowOutboundServerAction,
  setNewServerAction,
  newServerAction,
  activities,
  routes,
  addOutboundServerAction,
  showAddOutboundAction,
}) => {
  const handleHeadersChange = (value) => {
    setNewServerAction({ ...newServerAction, headers: value })
  }

  const handlePayloadChange = (value) => {
    setNewServerAction({ ...newServerAction, payload: value })
  }

  return (
    <DialogWrapper
      actions={null}
      open={showAddOutboundAction}
      onClose={() => setShowOutboundServerAction(false)}
      fullWidth
      maxWidth="md"
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
        <Box>
          <Typography variant="h6">
            {newServerAction.id ? 'Edit' : 'Add'} Outbound Server Action
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Configure an HTTP request to be triggered when a specific activity/route is completed
          </Typography>
        </Box>
      </Box>
      <Box>
        <FormControl
          size="small"
          variant="outlined"
          fullWidth
          color="primary"
          style={{ minWidth: 300, marginTop: 16 }}
        >
          <TextField
            sx={textFieldStyle}
            size="small"
            placeholder="Action Label"
            label="Action Label"
            value={newServerAction.label}
            onChange={(e) => setNewServerAction({ ...newServerAction, label: e.target.value })}
            helperText="A descriptive name for this server action"
          />
        </FormControl>
        <FormControl
          variant="outlined"
          fullWidth
          size="small"
          color="primary"
          style={{ minWidth: 300, marginTop: 16 }}
        >
          <Autocomplete
            sx={textFieldStyle}
            id="select-derived-field"
            options={activities.sort((a, b) =>
              a.process_template_name.localeCompare(b.process_template_name)
            )}
            size="small"
            getOptionLabel={(item) =>
              item?.process_template_name + '/' + item?.activity_template_name + ' (' + item?.id + ')'
            }
            value={
              activities?.find((it) => it.id === newServerAction.trigger_activity_template_id) ||
              null
            }
            onChange={(_, v) =>
              setNewServerAction({
                ...newServerAction,
                trigger_activity_template_id: v?.id,
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="Select Trigger Activity"
                color="primary"
                helperText="The activity that will trigger this server action"
              />
            )}
          />
        </FormControl>
        <FormControl
          variant="outlined"
          fullWidth
          size="small"
          color="primary"
          style={{ minWidth: 300, marginTop: 16 }}
        >
          <Autocomplete
            sx={textFieldStyle}
            id="select-derived-field"
            options={routes?.filter(
              (it) => it.from_activity_template_id === newServerAction.trigger_activity_template_id
            )}
            size="small"
            getOptionLabel={(item) => item?.route_template_name}
            value={routes?.find((it) => it.id === newServerAction.trigger_route_template_id)}
            onChange={(_, v) =>
              setNewServerAction({
                ...newServerAction,
                trigger_route_template_id: v?.id,
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="Select Triggering Route"
                color="primary"
                helperText="The route that will trigger this server action (optional)"
              />
            )}
          />
        </FormControl>
        <FormControl
          size="small"
          variant="outlined"
          fullWidth
          color="primary"
          style={{ minWidth: 300, marginTop: 16 }}
        >
          <TextField
            sx={textFieldStyle}
            size="small"
            placeholder="https://api.example.com/webhook"
            label="Endpoint URL"
            value={newServerAction.endpoint}
            onChange={(e) => setNewServerAction({ ...newServerAction, endpoint: e.target.value })}
            helperText="The full URL where the HTTP request will be sent"
          />
        </FormControl>
        <FormControl
          size="small"
          variant="outlined"
          fullWidth
          color="primary"
          style={{ minWidth: 300, marginTop: 16 }}
        >
          <Autocomplete
            size="small"
            sx={textFieldStyle}
            options={Object.keys(HTTP_METHOD).map((it) => ({
              value: it,
              label: it,
            }))}
            value={
              newServerAction.http_request_method
                ? {
                    value: newServerAction.http_request_method,
                    label: newServerAction.http_request_method,
                  }
                : null
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="HTTP Method"
                color="primary"
                helperText="The HTTP method to use for the request"
              />
            )}
            onChange={(_, v) =>
              setNewServerAction({ ...newServerAction, http_request_method: v?.value })
            }
          ></Autocomplete>
        </FormControl>
        <FormControl
          size="small"
          variant="outlined"
          fullWidth
          color="primary"
          style={{ minWidth: 300, marginTop: 16 }}
        >
          <TextField
            type="number"
            sx={textFieldStyle}
            size="small"
            placeholder="1"
            label="Sequence"
            value={newServerAction.sequence}
            onChange={(e) =>
              setNewServerAction({ ...newServerAction, sequence: Number(e.target.value) })
            }
            helperText="Order in which this action will execute (if multiple actions on same trigger)"
          />
        </FormControl>

        {/* Headers Editor */}
        <Box sx={{ mt: 3 }}>
          <PayloadEditor
            label="Headers (JSON)"
            value={newServerAction.headers}
            onChange={handleHeadersChange}
          />
        </Box>

        {/* Payload Editor */}
        <Box sx={{ mt: 3 }}>
          <PayloadEditor
            label="Payload / Request Body (JSON)"
            value={newServerAction.payload}
            onChange={handlePayloadChange}
          />
        </Box>

        <Box style={{ display: 'flex', justifyContent: 'flex-end', padding: '16px 0px', gap: 16 }}>
          <Button
            color="primary"
            variant="outlined"
            onClick={() => setShowOutboundServerAction(false)}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={() => addOutboundServerAction(newServerAction)}
          >
            {newServerAction.id ? 'Update' : 'Add'}
          </Button>
        </Box>
      </Box>
    </DialogWrapper>
  )
}

export default AddOutboundServerAction
